import type { Question } from '@/types/theme.types';
import { cn } from '@/lib/utils';
import type { InitialDataCopy } from '../initial-data-copy';
import { markerWindow } from '../initial-data.helpers';
import type { SessionTally } from '../types';

type QuestionMarkersProps = {
  copy: InitialDataCopy;
  questions: readonly Question[];
  currentIndex: number;
  done: ReadonlySet<string>;
  skipped: ReadonlySet<string>;
  tally: SessionTally;
};

type MarkerState = 'current' | 'done' | 'skipped' | 'todo';

const markerClass: Record<MarkerState, string> = {
  current: 'border-primary bg-primary text-primary-foreground',
  done: 'border-success/40 bg-success/10 text-success',
  skipped: 'border-brand/40 bg-brand/10 text-brand',
  todo: 'border-border/60 bg-surface text-faint-foreground',
};

/**
 * Footer of the question card: counts per state and a window of up to ten
 * numbered markers around the current question (sectors can be long).
 * Composed here because TaskStepDots has no "skipped" state and numbers
 * from 1 only.
 */
export function QuestionMarkers({ copy, questions, currentIndex, done, skipped, tally }: QuestionMarkersProps) {
  const visible = markerWindow(questions.length, currentIndex);
  const labels: Record<MarkerState, string> = {
    current: copy.legendCurrent,
    done: copy.legendDone,
    skipped: copy.legendSkipped,
    todo: copy.legendWaiting,
  };
  const legend = [
    { dot: 'bg-success', value: tally.submitted, label: copy.legendDone },
    { dot: 'bg-brand', value: tally.skipped, label: copy.legendSkipped },
    { dot: 'bg-faint-foreground', value: tally.remaining, label: copy.legendWaiting },
  ];

  return (
    <div className="space-y-3" aria-label={copy.progressLabel}>
      <ol className="flex flex-wrap items-center gap-1.5">
        {visible.map((index) => {
          const question = questions[index];
          const state: MarkerState =
            index === currentIndex ? 'current' : done.has(question.id) ? 'done' : skipped.has(question.id) ? 'skipped' : 'todo';
          return (
            <li key={question.id}>
              <span
                aria-current={state === 'current' ? 'step' : undefined}
                title={labels[state]}
                className={cn('grid size-7 place-items-center rounded-full border text-[11px] font-semibold tabular-nums', markerClass[state])}
              >
                {index + 1}
                <span className="sr-only">{` ${labels[state]}`}</span>
              </span>
            </li>
          );
        })}
      </ol>
      <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {legend.map((item) => (
          <span key={item.label} className="inline-flex items-center gap-1.5">
            <span className={cn('size-1.5 rounded-full', item.dot)} />
            <span className="font-medium tabular-nums text-foreground">{item.value}</span>
            {item.label}
          </span>
        ))}
      </p>
    </div>
  );
}
